import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Flex, Button, Text, Alert, AlertIcon } from "@chakra-ui/react";

import Pagination from "../../../../../shared/pagination/Pagination";
import Breadcrumbs from "../../../../../shared/breadcrumbs/Breadcrumbs";
import TableWrapper from "../../../../../shared/table/TableWrapper";
import theme from "../../../../../global/theme";

import GenericViewModal from "./GenericViewModal";
import GenericFormModal from "./GenericFormModal";
import GenericDeleteModal from "./GenericDeleteModal";

const ResourcePage = ({
  resource,        // "permissions" | "groups" | "roles" (key in state.roleManagement)
  singular,        // "permission" | "group" | "role" (used for translation keys)
  columns,
  viewFields,
  formFields = [],
  thunks,          // { fetch, create?, update?, remove? }
  paginated = true,
  readOnly = false,
  getInitialValues = (row) => row,
  toPayload = (values) => values,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const state = useSelector((s) => s.roleManagement[resource]);

  const [page, setPage] = useState(1);
  const [viewRow, setViewRow] = useState(null);
  const [form, setForm] = useState(null); // null | { row?: object }
  const [deleteRow, setDeleteRow] = useState(null);

  const refresh = () => dispatch(thunks.fetch({ page }));

  useEffect(() => {
    dispatch(thunks.fetch({ page }));
  }, [dispatch, thunks.fetch, page]);

  const tr = (key) => t(`pages.role_management.${key}_${singular}`);

  const handleFormSubmit = (values) => {
    const payload = toPayload(values);
    const action = form.row
      ? thunks.update({ _id: form.row._id, values: payload })
      : thunks.create(payload);
    return dispatch(action)
      .unwrap()
      .then(() => {
        refresh();
        setForm(null);
      })
      .catch(() => {});
  };

  const handleDelete = () =>
    dispatch(thunks.remove({ _id: deleteRow._id }))
      .unwrap()
      .then(() => {
        // if we deleted the last row of a page, step back one page
        if (page > 1 && state.data.length === 1) setPage(page - 1);
        else refresh();
      })
      .catch(() => {})
      .finally(() => setDeleteRow(null));

  const options = {
    showActions: true,
    actionIcons: readOnly ? ["view"] : ["view", "alwaysEdit", "delete"],
    page,
    // hideRowNumber: false,
  };

  const actions = {
    onView: setViewRow,
    onAlwaysEdit: (row) => setForm({ row }),
    onDelete: setDeleteRow,
  };

  return (
    <>
      <Breadcrumbs
        currentPage={t(`pages.role_management.${resource}`)}
        pages={[{ name: t("pages.dashboard.dashboard"), path: "/" }]}
      />

      {state.error && !form && !deleteRow && (
        <Alert status="error" variant="left-accent" marginBottom={8}>
          <AlertIcon />
          <Text>{state.error}</Text>
        </Alert>
      )}

      {!readOnly && (
        <Flex marginBottom={8}>
          <Button
            bg="red.600"
            textTransform="uppercase"
            fontWeight="bold"
            fontSize={18}
            color="white"
            _hover={{ bg: "red.600" }}
            onClick={() => setForm({})}
          >
            {t("general.create")}
          </Button>
        </Flex>
      )}

      {!state.isLoading && state.data.length > 0 ? (
        <TableWrapper columns={columns} data={state.data} options={options} actions={actions} />
      ) : (
        <Flex
          textAlign="center"
          bg={theme.light}
          boxShadow={theme.shadow}
          height={200}
          alignItems="center"
          justifyContent="center"
          borderRadius={16}
        >
          <Text fontSize={18} textTransform="capitalize" color="gray.300" fontWeight="bold">
            {t("general.no_data")}
          </Text>
        </Flex>
      )}

      {paginated && (
        <Flex justifyContent="center">
          <Pagination page={page} itemsCount={state.itemsCount ?? 0} onChange={setPage} />
        </Flex>
      )}

      {viewRow && (
        <GenericViewModal
          title={tr("view")}
          fields={viewFields}
          data={viewRow}
          onClose={() => setViewRow(null)}
        />
      )}

      {form && (
        <GenericFormModal
          title={form.row ? tr("edit") : tr("create")}
          fields={formFields}
          initialValues={form.row ? getInitialValues(form.row) : getInitialValues({})}
          isLoading={state.isLoading}
          error={state.error}
          onSubmit={handleFormSubmit}
          onClose={() => setForm(null)}
        />
      )}

      {deleteRow && (
        <GenericDeleteModal
          title={tr("delete")}
          name={deleteRow.name}
          isLoading={state.isLoading}
          onConfirm={handleDelete}
          onClose={() => setDeleteRow(null)}
        />
      )}
    </>
  );
};

export default ResourcePage;